/**
 * Contact modal — LIVE since the 2026-08 contact finish line (it shipped inert
 * on 2026-08-11 while delivery was an open question). Wired from the card's
 * Contact button, the experience drawer's footer, and the bulk bar's
 * "Contact all"; mailto links stay gone per client direction (2026-08-11).
 *
 * Sending: one mapsContactSend mutation with candidateIds only — the server
 * resolves every recipient address from the owned search snapshot and enforces
 * the delivery-safety mode (maps.contact.deliveryMode, default dryrun). A
 * non-'live' result is badged as a test delivery: success means the relay
 * accepted the batch, not that members received email.
 *
 * The discard guard promised by the inert version ships here: every exit path
 * (X / Escape / backdrop / Cancel) funnels through the drawer shell's single
 * Close(), which consults GuardClose — a dirty draft swaps the footer for an
 * INLINE confirm (a body-appended dialog would sit beneath the native top
 * layer, ui-best-practices §6), and a mid-send close is always vetoed.
 */
import { ChangeDetectorRef, Component, EventEmitter, Input, NgZone, OnChanges, Output, ViewChild } from '@angular/core';
import type { MapsCandidate, MapsContactSendResult } from '../../models/maps.types';
import { BuildRecipients, MergeRecipientResults, type ContactRecipient, type ContactRecipientRow } from '../../utils/recipients';
import { ContactService } from '../../services/contact.service';
import { NotifyError, NotifySuccess } from '../../services/notify';
import { DrawerComponent } from '../drawer/drawer.component';

@Component({
    selector: 'maps-contact-modal',
    standalone: false,
    templateUrl: './contact-modal.component.html',
    styleUrl: './contact-modal.component.scss',
})
export class ContactModalComponent implements OnChanges {
    /** One candidate = single contact; several = the bulk "Contact all" flow. */
    @Input({ required: true }) candidates: MapsCandidate[] = [];
    /** The owned search these candidates came from — required by the mutation
     *  (ownership check + server-side email resolution). */
    @Input({ required: true }) searchId: string | null = null;
    /** Per-send recipient cap (maps.contact.sendCap via the session payload). */
    @Input() cap = 25;
    @Output() closed = new EventEmitter<void>();
    /** Fires on a completed send so the search page can patch status chips. */
    @Output() sent = new EventEmitter<MapsContactSendResult>();

    @ViewChild('modal', { static: true }) private readonly modal!: DrawerComponent;

    recipients: ContactRecipient[] = [];
    title = 'Contact';
    subject = '';
    message = '';

    isSending = false;
    /** Non-null after a send whose outcome the user must review in-modal
     *  (any recipient not 'Sent'); the compose view swaps to results. */
    recipientRows: ContactRecipientRow[] | null = null;
    sendResult: MapsContactSendResult | null = null;
    /** Whole-mutation failure (network/GraphQL) — inline, draft retained. */
    errorMessage: string | null = null;
    confirmDiscard = false;

    constructor(
        private readonly contact: ContactService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnChanges(): void {
        this.recipients = BuildRecipients(this.candidates);
        this.title =
            this.recipients.length === 1
                ? `Contact ${this.recipients[0].name}`
                : `Contact ${this.recipients.length} candidates`;
    }

    get hasDraft(): boolean {
        return this.subject.trim().length > 0 || this.message.trim().length > 0;
    }

    get overCap(): boolean {
        return this.recipients.length > this.cap;
    }

    get overCapBy(): number {
        return this.recipients.length - this.cap;
    }

    get canSend(): boolean {
        return (
            !this.isSending &&
            this.sendResult === null &&
            this.searchId !== null &&
            this.recipients.length > 0 &&
            !this.overCap &&
            this.subject.trim().length > 0 &&
            this.message.trim().length > 0
        );
    }

    /** The send ran under a non-live safety mode — badge it as a test. */
    get isTestDelivery(): boolean {
        return this.sendResult !== null && this.sendResult.deliveryMode !== 'live';
    }

    get sentCount(): number {
        return this.sendResult?.recipients.filter((r) => r.status === 'Sent').length ?? 0;
    }

    /**
     * Close veto for the drawer shell ([closeGuard]). Arrow function so `this`
     * survives the input binding. Mid-send closes are always vetoed; a dirty
     * unsent draft surfaces the inline discard confirm; a spent draft (results
     * view) and a clean compose close freely.
     */
    readonly GuardClose = (): boolean => {
        if (this.isSending) return false;
        if (this.sendResult !== null || !this.hasDraft) return true;
        this.confirmDiscard = true;
        this.cdr.detectChanges();
        return false;
    };

    KeepEditing(): void {
        this.confirmDiscard = false;
    }

    Discard(): void {
        this.modal.Close(true);
    }

    Done(): void {
        this.modal.Close(true);
    }

    async Send(): Promise<void> {
        if (!this.canSend || this.searchId === null) return;
        this.isSending = true;
        this.errorMessage = null;
        this.confirmDiscard = false;
        try {
            const result = await this.contact.Send({
                searchId: this.searchId,
                candidateIds: this.recipients.map((r) => r.candidateId),
                subject: this.subject.trim(),
                body: this.message.trim(),
            });
            this.zone.run(() => {
                this.isSending = false;
                this.sendResult = result;
                this.sent.emit(result);
                const sent = result.recipients.filter((r) => r.status === 'Sent').length;
                const failed = result.recipients.length - sent;
                const testSuffix = result.deliveryMode !== 'live' ? ' (test mode — not delivered to candidates)' : '';
                if (failed === 0) {
                    NotifySuccess(`Message sent to ${sent} candidate${sent === 1 ? '' : 's'}${testSuffix}`);
                    this.modal.Close(true);
                } else {
                    // Partial (or total per-recipient) failure: stay open and
                    // show the per-recipient results so nothing fails silently.
                    this.recipientRows = MergeRecipientResults(this.recipients, result.recipients);
                    NotifyError(`Sent to ${sent} of ${result.recipients.length} — ${failed} failed`);
                    this.cdr.detectChanges();
                }
            });
        } catch (err) {
            this.zone.run(() => {
                this.isSending = false;
                this.errorMessage = (err as { message?: string })?.message ?? 'Sending failed — please try again';
                this.cdr.detectChanges();
            });
        }
    }

    /** Human wording for a per-recipient outcome (textual, never color-only). */
    StatusLabel(row: ContactRecipientRow): string {
        switch (row.status) {
            case 'Sent':
                return 'Sent';
            case 'NoEmail':
                return 'No email on file';
            case 'Blocked':
                return 'Blocked by delivery safety';
            case 'Failed':
                return 'Failed';
            default:
                return 'Not attempted';
        }
    }
}
